/**
 * Custom MCP connectors on an agent — the editor's side of
 * /chatbots/<id>/mcp-connectors. Every call needs edit rights on the agent;
 * a person who can only chat with it gets 403 and never sees the list.
 */

import api from '@services/api';
import type { ConnectorLibraryStatus } from '@modules/connector_library/services/connectorLibraryService';

export interface McpConnectorTool {
  name: string;
  description: string;
  /** False when the editor switched this tool off. */
  enabled: boolean;
  /** Whether it can change data in the service (switching it on asks first — item 69). */
  writes?: boolean;
}

export interface McpConnector {
  connector_id: string;
  chatbot_id: string;
  name: string;
  /** Tool-name prefix the model sees: `weather` → `weather__get_forecast`. */
  slug: string;
  url: string;
  transport: 'streamable_http' | 'sse';
  /** Whether an auth header is stored. The value itself is never returned. */
  has_headers: boolean;
  enabled: boolean;
  server_name: string | null;
  server_version: string | null;
  server_instructions: string | null;
  /** The editor's guide for the agent — in its instructions as written. */
  instructions: string;
  status: 'connected' | 'error';
  last_error: string | null;
  tools: McpConnectorTool[];
  tools_refreshed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** The entry of the organization's connector list this came from (null when added by hand). */
  library_entry_id?: string | null;
  library_version?: number | null;
  /** following / customized / detached; null when added by hand or before the list. */
  library_state?: 'following' | 'customized' | 'detached' | null;
  library_notice?: string | null;
  /** The list status (status chip, notice, key); null when not from the list. */
  library?: ConnectorLibraryStatus | null;
}

export interface CreateMcpConnectorPayload {
  name: string;
  url: string;
  headers?: Record<string, string>;
  instructions?: string;
  /** The person's acknowledgement of a connector added by hand (required). */
  acknowledge_risk?: boolean;
}

export interface UpdateMcpConnectorPayload {
  enabled?: boolean;
  disabled_tools?: string[];
  instructions?: string;
  /** Required when the change switches on tools that change data. */
  acknowledge_write?: boolean;
}

/** The backend's error body: `reason` is a machine key the UI can phrase. */
export interface McpConnectorErrorBody {
  error?: string;
  reason?: string;
  field?: string;
  connector?: McpConnector;
  /** RISK_NOT_ACKNOWLEDGED / WRITE_NOT_ACKNOWLEDGED (with `tools`, `service`). */
  error_code?: string;
  tools?: string[];
  service?: string;
}

const base = (chatbotId: string) => `/chatbots/${chatbotId}/mcp-connectors`;

class McpConnectorService {
  async list(chatbotId: string): Promise<McpConnector[]> {
    const { data } = await api.get(base(chatbotId));
    return data.connectors || [];
  }

  async create(chatbotId: string, payload: CreateMcpConnectorPayload): Promise<McpConnector> {
    const { data } = await api.post(base(chatbotId), payload);
    return data.connector;
  }

  async update(chatbotId: string, connectorId: string, payload: UpdateMcpConnectorPayload): Promise<McpConnector> {
    const { data } = await api.patch(`${base(chatbotId)}/${connectorId}`, payload);
    return data.connector;
  }

  async refresh(chatbotId: string, connectorId: string): Promise<McpConnector> {
    const { data } = await api.post(`${base(chatbotId)}/${connectorId}/refresh`);
    return data.connector;
  }

  async remove(chatbotId: string, connectorId: string): Promise<void> {
    await api.delete(`${base(chatbotId)}/${connectorId}`);
  }
}

export const mcpConnectorService = new McpConnectorService();
